import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';

import { submit, isSubmitting, isDirty, getFormValues } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import IconButton from 'components/common/IconButton';
import Button from 'components/common/Button';
import IconPen from 'components/icons/IconPen';

import primaryLanguages from 'config/primaryLanguages';

import { addNotification } from 'redux/modules/notifications';

import ContactInformationView from './ContactInformationView';
import ContactInformationEdit from './ContactInformationEdit';

const styles = theme => ({
  iconPen: {
    width: 16,
    height: 16,
  },
  button: {
    marginLeft: 20,
    '&:first-child': {
      marginLeft: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: 8,
    },
  },
  iconButton: {
    margin: -16,
    [theme.breakpoints.down('sm')]: {
      marginRight: -8,
    },
  },
  root: {
    position: 'relative',
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.short,
      }),
      top: -9,
      right: -9,
      bottom: -9,
      left: -9,
      borderRadius: 10,
      border: '2px solid transparent',
      pointerEvents: 'none',
    },
  },
  rootDirty: {
    '&:after': {
      borderColor: theme.palette.primary[500],
    },
  },
});

class ContactInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      edit: ['#contact-information', '#address-information'].indexOf(this.props.hash) !== -1,
    };

    this.onEditButtonClick = this.onEditButtonClick.bind(this);
    this.onCancelButtonClick = this.onCancelButtonClick.bind(this);
    this.onSaveButtonClick = this.onSaveButtonClick.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { patient, actions } = this.props;
    let message = '';
    if (prevProps.patient && patient
      && prevProps.patient['Address Information'] && patient['Address Information']
      && prevProps.patient['Address Information']['Is Rural'] !== patient['Address Information']['Is Rural']) {
      if (patient['Address Information']['Is Rural']) {
        message = message + 'Delivery Address changed from Urban to Rural.';
      }
      if (!patient['Address Information']['Is Rural']) {
        message = message + 'Delivery Address changed from Rural to Urban.';
      }
    }
    if (prevProps.patient && patient
      && prevProps.patient['Billing Address Information'] && patient['Billing Address Information']
      && prevProps.patient['Billing Address Information']['Is Rural'] !== patient['Billing Address Information']['Is Rural']) {
      if (patient['Billing Address Information']['Is Rural']) {
        message = message + ' Billing Address changed from Urban to Rural. Other Fee Schedule will be used in new Orders.';
      }
      if (!patient['Billing Address Information']['Is Rural']) {
        message = message + ' Billing Address changed from Rural to Urban. Other Fee Schedule will be used in new Orders.';
      }
    }
    if (message) {
      setTimeout(() => {
        actions.addNotification({
          message,
          variant: 'info',
        });
      }, 500);
    }
  }

  onEditButtonClick() {
    this.setState({ edit: true });
  }

  onCancelButtonClick() {
    this.setState({ edit: false });
  }

  onSaveButtonClick() {
    const { dirty, actions } = this.props;
    if (dirty) {
      actions.submit('patientContactInformation');
    } else {
      this.setState({ edit: false });
    }
  }

  render() {
    const {
      classes,
      patient,
      submitting,
      dirty,
      hash,
      formValues,
    } = this.props;
    const { edit } = this.state;
    let initialValues;
    if (patient !== null
      && Object.keys(patient['Contact Information']).length > 0
      && Object.keys(patient['Address Information']).length > 0) {
      initialValues = {
        ...patient['Contact Information'],
        ...patient['Address Information'],
        ...patient['Contact Information']['Preferred method of communications'] === 'By Email'
          ? { 'Email Notifications': true } : {},
        ...patient['Contact Information']['Preferred method of communications'] === 'By SMS'
          ? { 'SMS Notifications': true } : {},
        ...patient['Contact Information']['Preferred method of communications'] === 'Both'
          ? { 'SMS Notifications': true, 'Email Notifications': true  } : {},
        ...patient['Contact Information']['Preferred method of communications'] === 'None'
          ? { 'SMS Notifications': false, 'Email Notifications': false  } : {},
        'Billing Street': patient['Billing Address Information'].Street,
        'Billing Apt': patient['Billing Address Information'].Apt,
        'Billing Zip Code': patient['Billing Address Information']['Zip Code'],
        'Billing City': patient['Billing Address Information'].City,
        'Billing State': patient['Billing Address Information'].State,
        ...patient['Address Information'].Street === patient['Billing Address Information'].Street
          && patient['Address Information'].Apt === patient['Billing Address Information'].Apt
          && patient['Address Information']['Zip Code'] === patient['Billing Address Information']['Zip Code']
          && patient['Address Information'].City === patient['Billing Address Information'].City
          && patient['Address Information'].State === patient['Billing Address Information'].State
          ? { 'Use Billing Address': true } : { 'Use Billing Address': false },
        ...patient['Primary Language'] ? {
          'Primary Language': patient['Primary Language'],
          'Primary Language Name': primaryLanguages.find(item => item.code === patient['Primary Language']).name,
        } : {},
      };
    }
    return (
      <Section
        className={classNames(classes.root, {
          [classes.rootDirty]: dirty,
        })}
      >
        <SectionHeader>
          <SectionTitle>
            Contact Information
          </SectionTitle>
          <div>
            {(edit === true) ? (
              <Fragment>
                <Button
                  className={classes.button}
                  variant="raised"
                  disabled={!patient}
                  onClick={this.onCancelButtonClick}
                >
                  Cancel
                </Button>
                <Button
                  className={classes.button}
                  variant="raised"
                  color="primary"
                  disabled={!patient || submitting}
                  onClick={this.onSaveButtonClick}
                >
                  Save
                </Button>
              </Fragment>
            ) : (
              <IconButton
                className={classes.iconButton}
                onClick={this.onEditButtonClick}
                tooltipContent="Edit"
              >
                <IconPen className={classes.iconPen} />
              </IconButton>
            )}
          </div>
        </SectionHeader>
        <SectionBody>
          {(edit === true) ? (
            <ContactInformationEdit
              form="patientContactInformation"
              {... initialValues ? { initialValues } : {}}
              {... patient ? { 'Record Number': patient['Record Number'] } : {}}
              formValues={formValues}
              hash={hash}
              onSubmitSuccess={this.onCancelButtonClick}
            />
          ) : (
            <ContactInformationView {...patient} />
          )}
        </SectionBody>
      </Section>
    );
  }
}

ContactInformation.propTypes = {
  patient: PropTypes.shape({
    'Record Number': PropTypes.number.isRequired,
  }),
  submitting: PropTypes.bool.isRequired,
  dirty: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    submit: PropTypes.func.isRequired,
  }).isRequired,
  hash: PropTypes.string,
  classes: PropTypes.shape({
    button: PropTypes.string.isRequired,
  }).isRequired,
};

ContactInformation.defaultProps = {
  patient: null,
  hash: null,
};

const mapStateToProps = store => ({
  submitting: isSubmitting('patientContactInformation')(store),
  dirty: isDirty('patientContactInformation')(store),
  formValues: getFormValues('patientContactInformation')(store),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ submit, addNotification }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(ContactInformation),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/ContactInformation/ContactInformation.js